// WHAT: Header component for Kanban board screen
// WHY:  Provides title, description, and module manager button for Kanban view
// DOMAIN: Admin → Roadmap → Kanban
// DEPENDENCIES: Stack, Heading, Button components, Lucide icons
// RISKS:
//   - NOTE: No error handling if onModuleManager callback fails

import { LucideColumns, LucideSettings } from "lucide-react";
import React from "react";

import { Stack, Heading, Button } from "@/components/ui";

/**
 * Props for KanbanHeader component
 */
interface KanbanHeaderProperties {
  /** Callback to open module manager */
  onModuleManager: () => void;
}

/**
 * WHAT:
 *   Header component that displays the Kanban board title, description,
 *   and module manager button.
 *
 * WHY:
 *   Provides consistent header layout for Kanban screen with quick access
 *   to module management functionality.
 *
 * PROPS:
 *   - onModuleManager: () => void — Handler to open module manager
 *
 * BEHAVIOR:
 *   - Displays title and description
 *   - Shows module manager button
 *   - Displays view indicator (Kanban View)
 *
 * RISKS:
 *   - NOTE: onModuleManager callback should handle errors appropriately
 *
 * @param root0
 *
 * @param root0.onModuleManager
 *
 * @returns {JSX.Element} Die gerenderte KanbanHeader-Komponente
 *
 */
export const KanbanHeader: React.FC<KanbanHeaderProperties> = ({ onModuleManager }) => {
  return (
    <Stack align="center" direction="row" justify="between">
      <Stack direction="col" gap={1}>
        <Heading className="text-xl font-semibold text-white" level={2}>
          Kanban Board
        </Heading>
        <p className="text-slate-300">Status-based overview of all roadmap items</p>
      </Stack>
      <Stack align="center" direction="row" gap={2}>
        <Button
          className="flex items-center space-x-2"
          onClick={onModuleManager}
          size="sm"
          variant="outline"
        >
          <LucideSettings className="w-4 h-4" />
          <span>Module Manager</span>
        </Button>
        <Stack align="center" className="text-slate-300" direction="row" gap={2}>
          <LucideColumns className="w-4 h-4" />
          <span className="text-sm">Kanban View</span>
        </Stack>
      </Stack>
    </Stack>
  );
};
